import { useState, useEffect, useMemo } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import { format } from "date-fns";
import { listData } from "../services/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SubscriptionRow from "./subscriptionRow";

const HEADERS = [
  "Name",
  "Email",
  "Consent",
  "Newsletter",
  "Motivation Code",
  "Subscription Code",
  "Current Status",
  "Details",
  "Send Salesforce",
  "Last Updated",
];

const STATUS_TABS = [
  { key: "all", label: "Todos" },
  { key: "fulfilled", label: "Fulfilled" },
  { key: "pending", label: "Pendente" },
  { key: "needs_attention", label: "Needs Attention" },
];

export default function Subscriptions() {
  const params = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [subscriptions, setSubscriptions] = useState({
    title: undefined,
    data: [],
    count: state?.count || 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [emailSearch, setEmailSearch] = useState("");
  const [motivationCodeFilter, setMotivationCodeFilter] = useState("");
  const [motivationCodes, setMotivationCodes] = useState([]);
  const [registerFilter, setRegisterFilter] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  async function loadSubscriptions(
    nextCursor = "",
    register = registerFilter,
    status = statusFilter,
  ) {
    setIsLoading(true);
    const lookupTerm = register?.id ? "register_id" : "channel_id";
    const lookupValue = register?.id ? register.id : params.channelId ?? "";

    const response = await listData({
      table: "subscriptions",
      term: lookupTerm,
      value: lookupValue,
      limit: 20,
      cursor: nextCursor,
      status: status && status !== "all" ? status : undefined,
    });

    if (response instanceof Error) {
      console.error(response.message);
      setIsLoading(false);
      setHasMore(false);
      return;
    }

    const { data } = response;
    if (!Array.isArray(data)) {
      setIsLoading(false);
      setHasMore(false);
      return;
    }

    const newItems = data.filter((item) => !item.cursor && !item.count);

    if (!nextCursor && newItems.length <= 0) {
      setIsLoading(false);
      setSubscriptions((prev) => ({
        ...prev,
        title: params.channelId
          ? `Subscriptions from ${data[0]?.channel?.name || ""}`
          : "Subscriptions",
        data: [],
        count: 0,
      }));
      setHasMore(false);
      return;
    }

    const newCursor = data.find((item) => item.cursor);
    setCursor(newCursor ? newCursor.cursor : "");

    setSubscriptions((prev) => ({
      ...prev,
      title: params.channelId
        ? `Subscriptions from ${data[0]?.channel?.name || ""}`
        : "Subscriptions",
      data: nextCursor ? [...prev.data, ...newItems] : newItems,
      count:
        prev.count <= 0 ? data.find((item) => item.count)?.count : prev.count,
    }));

    setHasMore(!!newCursor);
    setIsLoading(false);
  }

  useEffect(() => {
    setCursor("");
    setHasMore(true);
    setSubscriptions((prev) => ({ ...prev, data: [], count: 0 }));
    loadSubscriptions("", registerFilter, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFilter, params.channelId, statusFilter]);

  useEffect(() => {
    if (!hasMore || isLoading) return;
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight
      ) {
        setIsLoading(true);
        loadSubscriptions(cursor);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line
  }, [cursor, hasMore, isLoading]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    async function loadMotivationCodes() {
      const response = await listData({
        table: "motivation-codes",
        term: "",
        value: "",
        limit: 0,
        cursor: "",
      });
      if (response instanceof Error) {
        console.error(response.message);
        return;
      }
      const codes = (response.data || []).filter((item) => !item.count);
      const filtered = params.channelId
        ? codes.filter((c) => c.channel_id === params.channelId)
        : codes;
      setMotivationCodes(filtered);
    }
    loadMotivationCodes();
  }, [params.channelId]);

  const filteredData = useMemo(() => {
    const term = emailSearch.trim().toLowerCase();
    return subscriptions.data.filter((item) => {
      if (
        motivationCodeFilter &&
        item.motivation_code_id !== motivationCodeFilter
      ) {
        return false;
      }
      if (term) {
        const email = (item.register?.email || "").toLowerCase();
        const name = (item.register?.name || "").toLowerCase();
        if (!email.includes(term) && !name.includes(term)) return false;
      }
      return true;
    });
  }, [subscriptions.data, motivationCodeFilter, emailSearch]);

  const stats = useMemo(() => {
    let fulfilled = 0;
    let pending = 0;
    let needsAttention = 0;
    let consent = 0;
    let newsletter = 0;
    subscriptions.data.forEach((item) => {
      const status = (item.current_status || "").toLowerCase();
      if (status.includes("fulfilled")) fulfilled += 1;
      else if (status.includes("needs_attention")) needsAttention += 1;
      else pending += 1;
      if (item.consent) consent += 1;
      if (item.newsletter) newsletter += 1;
    });
    return { fulfilled, pending, needsAttention, consent, newsletter };
  }, [subscriptions.data]);

  function handleRowResolved(updatedSub) {
    setSubscriptions((prev) => {
      if (statusFilter === "needs_attention" || statusFilter === "pending") {
        return {
          ...prev,
          data: prev.data.filter((item) => item.id !== updatedSub.id),
          count: prev.count > 0 ? prev.count - 1 : prev.count,
        };
      }
      return {
        ...prev,
        data: prev.data.map((item) =>
          item.id === updatedSub.id ? { ...item, ...updatedSub } : item,
        ),
      };
    });
  }

  function formatCsvValue(value) {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  function handleExportCsv() {
    setIsExporting(true);
    try {
      if (!filteredData.length) return;

      const csvHeaders = [
        "Name",
        "Email",
        "Consent",
        "Newsletter",
        "Motivation Code",
        "Subscription Code",
        "Current Status",
        "Last Updated",
      ];

      const rows = filteredData.map((item) => [
        item.register?.name || "",
        item.register?.email || "",
        item.consent ? "Sim" : "Não",
        item.newsletter ? "Sim" : "Não",
        item.motivation_code_id || "",
        item.subscription_code_id || "",
        item.current_status || "",
        item.updated_at
          ? format(new Date(item.updated_at), "dd/MM/yyyy HH:mm")
          : "",
      ]);

      const csv = [
        csvHeaders.map(formatCsvValue).join(","),
        ...rows.map((row) => row.map(formatCsvValue).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `subscriptions_${format(new Date(), "yyyyMMdd_HHmm")}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting subscriptions:", error);
    } finally {
      setIsExporting(false);
    }
  }

  const subscriptionRows = filteredData.map((item, index) => (
    <SubscriptionRow
      key={item.id || `${item.register_id}-${index}`}
      index={index}
      args={item}
      onFilterByRegister={(sub) =>
        setRegisterFilter({ id: sub.register_id, email: sub.register?.email })
      }
      onResolved={handleRowResolved}
    />
  ));

  return (
    <div className="w-full max-w-[1400px] mx-auto px-6 pb-8">
      <div className="w-full flex items-center justify-between py-6 border-b border-slate-200 mb-6">
        <div className="flex flex-col">
          <span className="text-slate-500 text-xs uppercase tracking-wider">
            Ferramentas
          </span>
          <h1 className="text-slate-900 text-2xl font-bold tracking-tight m-0">
            {subscriptions.title ?? "Subscriptions"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => navigate(-1)} variant="outline-secondary">
            Back
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="p-4 border-0 shadow-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
            Total
          </div>
          <div className="text-3xl font-bold text-slate-900 mt-2 tabular-nums">
            {subscriptions.count || 0}
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
            Fulfilled
          </div>
          <div className="text-3xl font-bold text-emerald-700 mt-2 tabular-nums">
            {stats.fulfilled}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            dos {subscriptions.data.length} carregados
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
            Pendente
          </div>
          <div className="text-3xl font-bold text-amber-700 mt-2 tabular-nums">
            {stats.pending}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            dos {subscriptions.data.length} carregados
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
            Needs Attention
          </div>
          <div className="text-3xl font-bold text-rose-700 mt-2 tabular-nums">
            {stats.needsAttention}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            dos {subscriptions.data.length} carregados
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
            Consent / Newsletter
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-2 tabular-nums">
            {stats.consent} / {stats.newsletter}
          </div>
          <div className="text-[11px] text-slate-400 mt-1">
            dos {subscriptions.data.length} carregados
          </div>
        </Card>
      </div>

      <Card className="p-4 border-0 shadow-sm mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 flex-1 min-w-[240px]">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Buscar por nome ou email
            </label>
            <input
              type="text"
              className="form-control"
              placeholder="Digite para filtrar..."
              value={emailSearch}
              onChange={(event) => setEmailSearch(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[240px]">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Motivation Code
            </label>
            <select
              className="form-select"
              value={motivationCodeFilter}
              onChange={(event) => setMotivationCodeFilter(event.target.value)}
            >
              <option value="">Todos</option>
              {motivationCodes.map((code) => (
                <option key={code.motivation_code} value={code.motivation_code}>
                  {code.motivation_code}
                  {code.description ? ` — ${code.description}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant="outline-primary"
              onClick={handleExportCsv}
              disabled={!filteredData.length || isExporting}
            >
              {isExporting ? <Spinner size="sm" /> : "Exportar CSV"}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="inline-flex rounded-lg bg-white border border-slate-200 p-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === tab.key
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {registerFilter && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-sm">
                <span>
                  Registro:{" "}
                  <strong>{registerFilter.email || registerFilter.id}</strong>
                </span>
                <button
                  type="button"
                  aria-label="Limpar filtro de registro"
                  className="font-bold text-blue-900 hover:text-blue-700 leading-none bg-transparent border-0 p-0"
                  onClick={() => setRegisterFilter(null)}
                >
                  ×
                </button>
              </div>
            )}
            <span className="text-sm text-slate-500 tabular-nums">
              {filteredData.length}/{subscriptions.count || 0} registros
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">
                  #
                </th>
                {HEADERS.map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{subscriptionRows}</tbody>
          </table>
        </div>
        {isLoading && (
          <div className="w-full text-center py-6">
            <Spinner />
          </div>
        )}
        {!hasMore && !isLoading && filteredData.length > 0 && (
          <div className="w-full text-center py-3 text-sm text-slate-500 border-t border-slate-100">
            Fim dos registros
          </div>
        )}
        {filteredData.length <= 0 && !isLoading && (
          <div className="w-full text-center py-10 text-slate-500">
            Nenhuma inscrição encontrada.
          </div>
        )}
      </Card>
    </div>
  );
}
